import { authFetch } from './auth';

export interface SiteSummary {
  id: number;
  slug: string;
  name: string;
  created_at: string;
  category_count: number;
  active_item_count: number;
}

export interface CategorySummary {
  id: number;
  slug: string;
  name: string;
  active_item_count: number;
}

export interface Item {
  id: number;
  category_id: number;
  title: string;
  thumbnail_url: string | null;
  link_url: string | null;
  sort_order: number;
  is_active: number;
  meta_json: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ChangeLog {
  id: number;
  item_id: number | null;
  actor_email: string;
  action: 'create' | 'update' | 'delete' | 'reorder';
  diff_json: string | null;
  created_at: string;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authFetch(path, init);
  const text = await res.text();
  const data = text ? (JSON.parse(text) as T & { success?: boolean; message?: string }) : ({} as T);
  if (!res.ok) {
    const msg = (data as { message?: string }).message ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

const jsonInit = (body: unknown, method: string): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const api = {
  listSites: () => request<{ sites: SiteSummary[] }>('/api/admin/sites').then((d) => d.sites),
  createSite: (slug: string, name: string) =>
    request<{ id: number; slug: string; name: string }>('/api/admin/sites', jsonInit({ slug, name }, 'POST')),

  listCategories: (siteId: number) =>
    request<{ categories: CategorySummary[] }>(`/api/admin/sites/${siteId}/categories`).then(
      (d) => d.categories
    ),
  createCategory: (siteId: number, slug: string, name: string) =>
    request<{ id: number }>(
      `/api/admin/sites/${siteId}/categories`,
      jsonInit({ slug, name }, 'POST')
    ),

  listItems: (categoryId: number) =>
    request<{ items: Item[] }>(`/api/admin/items?category_id=${categoryId}`).then((d) => d.items),
  createItem: (body: Partial<Item> & { category_id: number; title: string }) =>
    request<{ id: number }>('/api/admin/items', jsonInit(body, 'POST')),
  updateItem: (id: number, body: Partial<Item>) =>
    request<{ id: number; updated: boolean }>(`/api/admin/items/${id}`, jsonInit(body, 'PUT')),
  deleteItem: (id: number, hard = false) =>
    request<{ id: number; deleted: boolean }>(
      `/api/admin/items/${id}${hard ? '?hard=1' : ''}`,
      { method: 'DELETE' }
    ),
  reorderItems: (categoryId: number, order: number[]) =>
    request<{ category_id: number; count: number; reordered: boolean }>(
      '/api/admin/items/reorder',
      jsonInit({ category_id: categoryId, order }, 'POST')
    ),

  listChangeLog: (limit = 20, itemId?: number) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (itemId !== undefined) qs.set('item_id', String(itemId));
    return request<{ logs: ChangeLog[] }>(`/api/admin/change-log?${qs}`).then((d) => d.logs);
  },

  me: () => request<{ user: { email: string; level: number; name?: string } }>('/api/admin/me'),
};
